import React, { useMemo } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useField } from 'formik'
import { ChargeStrategy, WallboxConfigurationModel } from 'src/Libs/wallboxes'
import { VariantTypes as WeekdayVariantTypes } from 'src/Libs/wallboxes/components/form/WeekDaysSettingList'
import { KeyLockMode } from 'src/Libs/wallboxes/types'
import { UserLevel } from 'src/State/User/UserModel'
import SettingsBox from 'src/Elements/Data/SettingsBox'
import MaxChargeCurrentLimitSet from '../components/form/MaxChargeCurrentLimitSet'
import { WallboxType } from 'src/Libs/wallboxes/api'
import { useMediaQuery, useTheme } from '@mui/material'
import { CommonFieldNames, CommonFieldLabels, CommonFieldTitles } from '../components'

interface UseWallboxSettingsFormHelperHooks {
  weekdayVariant: WeekdayVariantTypes
  doDisplayWeekdays: boolean
  activeChargeStrategies: { label: string; value: ChargeStrategy }[]
  keyLockModes: { label: string; value: KeyLockMode }[]
  maxChargeCurrentLimitSetBuilder: (userLevel: UserLevel, disabled: boolean) => React.ReactElement
  areNetworkSettingsVisible: boolean
  areNetworkSettingsChangeable: boolean
  isMobile: boolean
  isDesktop: boolean
}

const useWallboxSettingsFormHelpers = (model: WallboxConfigurationModel): UseWallboxSettingsFormHelperHooks => {
  const { t } = useCustomTranslation()
  const { breakpoints } = useTheme()
  const isDownSm = useMediaQuery(breakpoints.down('sm'))

  const [activeChargeStrategySwitch] = useField<ChargeStrategy>('activeChargeStrategy')
  const [dhcpEnabledField] = useField<boolean>('dhcpEnabled')

  const weekdayVariant = useMemo<WeekdayVariantTypes>(() => {
    if (activeChargeStrategySwitch.value === 'delayed') {
      return 'full'
    }
    return 'voltage'
  }, [activeChargeStrategySwitch.value])

  const doDisplayWeekdays = useMemo<boolean>(() => {
    return activeChargeStrategySwitch.value !== 'deactivated'
  }, [activeChargeStrategySwitch.value])

  const areNetworkSettingsVisible = useMemo<boolean>(() => {
    return [WallboxType.EasyConnect, WallboxType.Bidi].includes(model.wallboxType)
  }, [model.wallboxType])

  const areNetworkSettingsChangeable = useMemo<boolean>(() => {
    return !dhcpEnabledField.value
  }, [dhcpEnabledField.value])

  const activeChargeStrategies = [
    { label: t('systemSettings.wallbox.settings.activeChargeStrategy.option.now'), value: 'now' },
    { label: t('systemSettings.wallbox.settings.activeChargeStrategy.option.delayed'), value: 'delayed' },
    { label: t('systemSettings.wallbox.settings.activeChargeStrategy.option.deactivated'), value: 'deactivated' },
  ] as { label: string; value: ChargeStrategy }[]

  const keyLockModes = [
    { label: t('systemSettings.wallbox.settings.keyLockMode.option.lock'), value: 'lock' },
    { label: t('systemSettings.wallbox.settings.keyLockMode.option.sun'), value: 'sun' },
  ] as { label: string; value: KeyLockMode }[]

  const maxChargeCurrentLimitSetBuilder = (userLevel: UserLevel, disabled = false): React.ReactElement => {
    const readonly = userLevel !== 'INSTALLER'
    return (
      <SettingsBox
        disabled={disabled}
        title={t(CommonFieldTitles.maxChargeCurrentLimit)}
        suffix={
          <MaxChargeCurrentLimitSet
            maxChargeLabel={t(CommonFieldLabels.maxChargeCurrent)}
            maxChargeName={CommonFieldNames.maxChargeCurrent}
            limitName={CommonFieldNames.upperCurrentLimit}
            limitLabel={t(CommonFieldLabels.upperCurrentLimit)}
            disableLimit={readonly || disabled}
            disabledMax={disabled}
          />
        }
      />
    )
  }

  return {
    activeChargeStrategies,
    weekdayVariant,
    doDisplayWeekdays,
    maxChargeCurrentLimitSetBuilder,
    keyLockModes,
    areNetworkSettingsChangeable,
    areNetworkSettingsVisible,
    isMobile: isDownSm,
    isDesktop: !isDownSm,
  }
}

export default useWallboxSettingsFormHelpers
